import type { ReactElement } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import * as Icons from "@fortawesome/free-solid-svg-icons"
import "@wfp/ui/src/globals/scss/styles.scss"
import CategoriesPage from "pages/Categories/CategoriesPage"
import CommoditiesPage from "pages/CommoditiesPages/CommoditiesPage"
import DashboardPage from "pages/Markets/DashboardPage"
import PricetypesPage from "pages/Pricetypes/PricetypesPage"
import ReportsPage from "pages/Reports/ReportsPage"
import UsersPage from "pages/Users/UsersPage"
import { HelmetProvider } from "react-helmet-async"
import { useMsal } from "@azure/msal-react"
import { CustomNavigationClient } from "lib/CustomNavigationClient"
import { useEffect } from "react"
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from "react-router-dom"
import { HH_MODULE, MARKET_MODULE, MFI_MODULE, RPME_MODULE, GORP_MODULE } from "constants/constants"
import HelmetWrapper from "routes/HelmetWrapper"
import NotFound from "routes/NotFound"
import PrivateOutlet from "routes/PrivateOutlet"
import ProtectedRoute from "routes/ProtectedRoute"
import RootRoute from "routes/RootRoute"
import Reports from "pages/GorpPage/Reports"
import ViewData from "pages/GorpPage/ViewData"
import ViewDataRegional from "pages/GorpPage/ViewDataRegional"
import AdminUnit1 from "pages/AdminUnit1/AdminUnit1"
import AdminUnit2 from "pages/AdminUnit2/AdminUnit2"
import CommoditiesUnitPage from "pages/CommoditiesUnit/CommoditiesUnitPage"
import XlsFormPage from "pages/XlsForm/XlsFormPage"
import RPMEXlsForm from "pages/XlsForm/RPMEXlsForm"
import AddXlsFormSchema from "pages/XlsForm/AddXlsFormSchema"
import RPMEAddXlsFormSchema from "pages/XlsForm/RPMEAddXlsFormSchema"
import Series from "pages/Series/Series"
import UploadGorpReportExcel from "pages/GorpPage/UploadExcel"
import SnackBarComponent from "components/Shared/Common/SnackBar"
import CommoditiesBasket from "pages/CommoditiesPages/CommoditiesBasket"
import AboutRpme from "pages/DetailsPages/AboutRpme"
import HHXlsForm from "pages/HouseHold/XlsFormPage"
import HHAddSurvey from "pages/HouseHold/AddSurvey"
import HHAddBaseXlsSchema from "pages/HouseHold/AddBaseXlsSchema"
import HHAddXlsFormSchema from "pages/HouseHold/AddXlsFormSchema"
import HHSurveyPage from "pages/HouseHold/SurveyPage"
import HHBaseXlsform from "pages/HouseHold/BaseXlsform"
import NoAccessPage from "pages/Auth/NoAccess"
import UserProfilePage from "pages/Users/UserProfile"
import AddAsset from "pages/Aims/AddAsset"
import AssetListPage from "pages/Aims/AssetListPage"
import AssetFormPage from "pages/Aims/AssetFormPage"
import AssetPreviewPage from "pages/Aims/AssetPreviewPage"
import ADAListPage from "pages/Aims/ADAListPage"
import LIAListPage from "pages/Aims/LIAListPage"
import ContractDetailsPage from "pages/Aims/ContractDetailsPage"
import AimsSettingsPage from "pages/Aims/SettingsPage"
import ContractTypesTab from "pages/Aims/Settings/ContractTypesTab"
import AssetCategoryTab from "pages/Aims/Settings/AssetCategoryTab"
import AssetSubcategoryTab from "pages/Aims/Settings/AssetSubcategoryTab"
import AssetDetailsTab from "pages/Aims/Settings/AssetDetailsTab"
import OutcomesTab from "pages/Aims/Settings/OutcomesTab"
import SurveyRPME from "pages/Survey/SurveyRPME"
import SurveyMFI from "pages/Survey/SurveyMFI"
import AddSurveyMFI from "pages/Survey/AddSurveyMFI"
import AddSurveyRPME from "pages/Survey/AddSurveyRPME"
import AlpsPage from "pages/ALPS/AlpsPage"
import MarketsPage from "pages/Markets/MarketsPage"
import CurrencyPage from "pages/Currencies/CurrencyPage"
import SurveyDetailsPage from "pages/HouseHold/Details/SurveyDetailsPage"
import SettingsPage from "pages/HouseHold/Settings/SettingsPage"
import BaseXlsFormPage from "pages/HouseHold/BaseXlsForm/BaseXlsFormPage"
import XlsFormPageHH from "pages/HouseHold/XslForm/XlsFormPageHH"
import AboutHHPage from "pages/HouseHold/About/AboutHHPage"
import MFIAddBaseXlsSchema from "./pages/BaseXlsForm/MFIAddBaseXlsSchema"
import Outliers from "./pages/Outliers/OutliersPage"
import UploadReportExcel from "./components/Reports/UploadExcel"
import MappingSchemaListPage from "./pages/MappingSchema/MappingSchemaListPage"
import MappingSchemaPage from "./pages/MappingSchema/MappingSchemaPage"
import DataUploadPage from "./pages/Markets/DataUploadPage"
import DataUploadProvider from "./providers/DataUploadContext"
import ModaUploadPage from "./pages/Markets/ModaUploadPage"
import ModaEditPage from "./pages/Markets/ModaEditPage"
import ReportPage from "./pages/Reports/ReportPage"
import { DataModaUploadProvider } from "./providers/DataModaUploadContext"
import ModaValidation from "./pages/Markets/ModaValidation"
import ContractsPage from "./pages/Aims/ContractsPage"
import RPMEBaseXlsForm from "./pages/BaseXlsForm/RPMEBaseXlsForm"
import MFIBaseXlsForm from "./pages/BaseXlsForm/MFIBaseXlsForm"
import RPMEAddBaseXlsSchema from "./pages/BaseXlsForm/RPMEAddBaseXlsSchema"

const iconList = Object.keys(Icons)
  .filter(key => key !== "fas" && key !== "prefix")
  // @ts-ignore
  .map(icon => Icons[icon])

library.add(...iconList)

const withProtectedRoute = (element: ReactElement, moduleId: number): ReactElement => {
  return <ProtectedRoute moduleId={moduleId}>{element}</ProtectedRoute>
}

const MsalNavigationWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()
  const { instance } = useMsal()

  useEffect(() => {
    const navigationClient = new CustomNavigationClient(navigate)
    instance.setNavigationClient(navigationClient)
  }, [instance, navigate])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

function App() {
  return (
    <HelmetProvider>
      <SnackBarComponent />
      <Router>
        <MsalNavigationWrapper>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/no-access" element={<NoAccessPage />} />
            <Route path="/market-prices" element={withProtectedRoute(<PrivateOutlet />, MARKET_MODULE)}>
              <Route
                path="dashboard"
                element={
                  <HelmetWrapper title="Dashboard" content="dashboard">
                    <DashboardPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="data-upload/:rId?"
                element={
                  <HelmetWrapper title="Data Upload" content="data-upload">
                    <DataUploadProvider>
                      <DataUploadPage />
                    </DataUploadProvider>
                  </HelmetWrapper>
                }
              />
              <Route
                path="moda-data-upload"
                element={
                  <HelmetWrapper title="Data Upload" content="moda-upload">
                    <DataModaUploadProvider>
                      <ModaUploadPage />
                    </DataModaUploadProvider>
                  </HelmetWrapper>
                }
              />
              <Route
                path="moda-data-upload/edit/:sId"
                element={
                  <HelmetWrapper title="Data Upload" content="moda-upload">
                    <DataModaUploadProvider>
                      <ModaEditPage />
                    </DataModaUploadProvider>
                  </HelmetWrapper>
                }
              />
              <Route
                path="moda-data-upload/:rId"
                element={
                  <HelmetWrapper title="Moda Data Upload" content="moda-data-upload">
                    <DataUploadProvider>
                      <ModaValidation />
                    </DataUploadProvider>
                  </HelmetWrapper>
                }
              />
              <Route
                path="reports"
                element={
                  <HelmetWrapper title="Reports" content="reports">
                    <ReportsPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="reports/excel/:reportId"
                element={
                  <HelmetWrapper title="Upload report" content="upload-report">
                    <UploadReportExcel />
                  </HelmetWrapper>
                }
              />
              <Route
                path="reports/:rId"
                element={
                  <HelmetWrapper title="Report details" content="view-report">
                    <ReportPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="outliers"
                element={
                  <HelmetWrapper title="Outliers" content="outliers">
                    <Outliers />
                  </HelmetWrapper>
                }
              />
              <Route
                path="commodities"
                element={
                  <HelmetWrapper title="Commodities" content="commodities">
                    <CommoditiesPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="Categories"
                element={
                  <HelmetWrapper title="Categories" content="categories">
                    <CategoriesPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="priceTypes"
                element={
                  <HelmetWrapper title="Price Types" content="price-types">
                    <PricetypesPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="currencies"
                element={
                  <HelmetWrapper title="Currencies" content="currencies">
                    <CurrencyPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="ALPS"
                element={
                  <HelmetWrapper title="ALPS" content="ALPS">
                    <AlpsPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="seriesCombinations"
                element={
                  <HelmetWrapper title="Series" content="series">
                    <Series />
                  </HelmetWrapper>
                }
              />
              <Route
                path="Units-Of-Measure"
                element={
                  <HelmetWrapper title="Units of Measure" content="units-of-measure">
                    <CommoditiesUnitPage />
                  </HelmetWrapper>
                }
              />

              <Route
                path="mappingSchema"
                element={
                  <HelmetWrapper title="Mapping Schema" content="mapping-schema">
                    <MappingSchemaListPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="mappingSchema/add"
                element={
                  <HelmetWrapper title="Create Mapping Schema" content="create-mapping-schema">
                    <DataUploadProvider>
                      <MappingSchemaPage />
                    </DataUploadProvider>
                  </HelmetWrapper>
                }
              />
              <Route
                path="mappingSchema/edit/:id"
                element={
                  <HelmetWrapper title="Modify Mapping Schema" content="modify-mapping-schema">
                    <DataUploadProvider>
                      <MappingSchemaPage />
                    </DataUploadProvider>
                  </HelmetWrapper>
                }
              />
            </Route>

            <Route path="/rpme" element={withProtectedRoute(<PrivateOutlet />, RPME_MODULE)}>
              <Route
                path="about-rpme"
                element={
                  <HelmetWrapper title="About RPME" content="about-rpme">
                    <AboutRpme />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys/add"
                element={
                  <HelmetWrapper title="Add RPME Survey" content="add-rpme-surveys">
                    <AddSurveyRPME />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys"
                element={
                  <HelmetWrapper title="Surveys" content="surveys">
                    <SurveyRPME />
                  </HelmetWrapper>
                }
              />
              <Route
                path="BaseXlsFormSchema/add"
                element={
                  <HelmetWrapper title="New Base XLSForm Schema Upload" content="add-base-xls-schema">
                    <RPMEAddBaseXlsSchema />
                  </HelmetWrapper>
                }
              />
              <Route
                path="BaseXlsFormSchema"
                element={
                  <HelmetWrapper title="Xls Form Schema" content="xls-form-schema">
                    <RPMEBaseXlsForm />
                  </HelmetWrapper>
                }
              />
              <Route
                path="XlsFormSchema/add"
                element={
                  <HelmetWrapper title="Add XLSForm Schema" content="add-xls-form-schema">
                    <RPMEAddXlsFormSchema />
                  </HelmetWrapper>
                }
              />
              <Route
                path="XlsFormSchema"
                element={
                  <HelmetWrapper title="Xls Form Schema" content="xls-form-schema">
                    <RPMEXlsForm />
                  </HelmetWrapper>
                }
              />
              <Route
                path="CommodityBasket"
                element={
                  <HelmetWrapper title="Commodity Basket" content="commodity-basket">
                    <CommoditiesBasket />
                  </HelmetWrapper>
                }
              />
            </Route>

            <Route path="/mfi" element={withProtectedRoute(<PrivateOutlet />, MFI_MODULE)}>
              <Route
                path="XlsFormSchema"
                element={
                  <HelmetWrapper title="Xls Form Schema" content="xls-form-schema">
                    <XlsFormPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="XlsFormSchema/add"
                element={
                  <HelmetWrapper title="Add XLSForm Schema" content="add-xls-form-schema">
                    <AddXlsFormSchema />
                  </HelmetWrapper>
                }
              />
              <Route
                path="BaseXlsFormSchema"
                element={
                  <HelmetWrapper title="Xls Form Schema" content="xls-form-schema">
                    <MFIBaseXlsForm />
                  </HelmetWrapper>
                }
              />
              <Route
                path="BaseXlsFormSchema/add"
                element={
                  <HelmetWrapper title="New Base XLSForm Schema Upload" content="add-base-xls-form-schema">
                    <MFIAddBaseXlsSchema />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys"
                element={
                  <HelmetWrapper title="Surveys" content="surveys">
                    <SurveyMFI />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys/add"
                element={
                  <HelmetWrapper title="Add Survey" content="add-survey">
                    <AddSurveyMFI />
                  </HelmetWrapper>
                }
              />
            </Route>

            <Route path="/household" element={withProtectedRoute(<PrivateOutlet />, HH_MODULE)}>
              <Route
                path="about"
                element={
                  <HelmetWrapper title="About Household" content="about-household">
                    <AboutHHPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys/add"
                element={
                  <HelmetWrapper title="Add HH Survey" content="add-hh-surveys">
                    <HHAddSurvey />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys/:surveyId"
                element={
                  <HelmetWrapper title="View HH Survey" content="view-hh-survey">
                    <SurveyDetailsPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="surveys"
                element={
                  <HelmetWrapper title="Surveys" content="surveys">
                    <HHSurveyPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="baseXlsFormSchema/:baseXlsFormId"
                element={
                  <HelmetWrapper title="View HH Base XLSForm" content="view-hh-base-xls">
                    <BaseXlsFormPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="BaseXlsFormSchema/add"
                element={
                  <HelmetWrapper title="New Base XLSForm Schema Upload" content="add-base-xls-schema">
                    <HHAddBaseXlsSchema />
                  </HelmetWrapper>
                }
              />
              <Route
                path="BaseXlsFormSchema"
                element={
                  <HelmetWrapper title="Xls Form Schema" content="xls-form-schema">
                    <HHBaseXlsform />
                  </HelmetWrapper>
                }
              />
              <Route
                path="xlsFormSchema/:xlsFormId"
                element={
                  <HelmetWrapper title="View HH XLSForm" content="view-hh-xls">
                    <XlsFormPageHH />
                  </HelmetWrapper>
                }
              />
              <Route
                path="XlsFormSchema/add"
                element={
                  <HelmetWrapper title="Add XLSForm Schema" content="add-xls-form-schema">
                    <HHAddXlsFormSchema />
                  </HelmetWrapper>
                }
              />
              <Route
                path="XlsFormSchema"
                element={
                  <HelmetWrapper title="Xls Form Schema" content="xls-form-schema">
                    <HHXlsForm />
                  </HelmetWrapper>
                }
              />
              <Route
                path="settings"
                element={
                  <HelmetWrapper title="HH Settings" content="hh-settings">
                    <SettingsPage />
                  </HelmetWrapper>
                }
              />
            </Route>

            <Route path="/gorp" element={withProtectedRoute(<PrivateOutlet />, GORP_MODULE)} />
            <Route path="/aims" element={<PrivateOutlet />}>
              <Route
                path="AssetsView/add"
                element={
                  <HelmetWrapper title="Add Asset" content="add-assets">
                    <AddAsset />
                  </HelmetWrapper>
                }
              />
              <Route
                path="AssetsView/add-manual"
                element={
                  <HelmetWrapper title="Add Manual Asset" content="add-manual-assets">
                    <AssetFormPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="AssetsView"
                element={
                  <HelmetWrapper title="Assets" content="assets">
                    <AssetListPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="AssetsView/preview/:id"
                element={
                  <HelmetWrapper title="Asset Preview" content="asset-preview">
                    <AssetPreviewPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="AssetsView/edit/:id"
                element={
                  <HelmetWrapper title="Edit Asset" content="edit-asset">
                    <AssetFormPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="ADA"
                element={
                  <HelmetWrapper title="ADA Rounds" content="ada-rounds">
                    <ADAListPage />
                  </HelmetWrapper>
                }
              />
              <Route path="AD" element={<Navigate to="/aims/ADA" replace />} />
              <Route
                path="LIA"
                element={
                  <HelmetWrapper title="LIA Rounds" content="lia-rounds">
                    <LIAListPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="Contracts"
                element={
                  <HelmetWrapper title="Contracts" content="aims-contracts">
                    <ContractsPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="Contracts/details/:id"
                element={
                  <HelmetWrapper title="Contract Details" content="aims-contract-details">
                    <ContractDetailsPage />
                  </HelmetWrapper>
                }
              />
              <Route
                path="Settings"
                element={
                  <HelmetWrapper title="Settings" content="aims-settings">
                    <AimsSettingsPage />
                  </HelmetWrapper>
                }
              >
                <Route index element={<Navigate to="asset-category" replace />} />
                <Route path="asset-category" element={<AssetCategoryTab />} />
                <Route path="asset-subcategory" element={<AssetSubcategoryTab />} />
                <Route path="asset-details" element={<AssetDetailsTab />} />
                <Route path="outcomes" element={<OutcomesTab />} />
                <Route path="contract-types" element={<ContractTypesTab />} />
              </Route>
            </Route>

            <Route path="/gorp" element={<PrivateOutlet />}>
              <Route
                path="gorpReports"
                element={
                  <HelmetWrapper title="report" content="report">
                    <Reports />
                  </HelmetWrapper>
                }
              />
              <Route
                path="gorpReports/excel/:reportId"
                element={
                  <HelmetWrapper title="Upload report" content="upload-report">
                    <UploadGorpReportExcel />
                  </HelmetWrapper>
                }
              />
              <Route
                path="gorpViewData"
                element={
                  <HelmetWrapper title="viewdata" content="viewdata">
                    <ViewData />
                  </HelmetWrapper>
                }
              />
              <Route
                path="gorpViewDataRegional"
                element={
                  <HelmetWrapper title="viewdataregional" content="viewdataregional">
                    <ViewDataRegional />
                  </HelmetWrapper>
                }
              />
            </Route>
            <Route path="/AdminUnitsL1" element={<PrivateOutlet />}>
              <Route
                path="/AdminUnitsL1"
                element={
                  <HelmetWrapper title="AdminUnitsL1" content="adminUnitsL1">
                    <AdminUnit1 />
                  </HelmetWrapper>
                }
              />
            </Route>
            <Route path="/AdminUnitsL2" element={<PrivateOutlet />}>
              <Route
                path="/AdminUnitsL2"
                element={
                  <HelmetWrapper title="AdminUnitsL2" content="adminUnitsL2">
                    <AdminUnit2 />
                  </HelmetWrapper>
                }
              />
            </Route>
            <Route path="/markets" element={<PrivateOutlet />}>
              <Route
                path="/markets"
                element={
                  <HelmetWrapper title="Markets" content="markets">
                    <MarketsPage />
                  </HelmetWrapper>
                }
              />
            </Route>
            <Route path="/users" element={<PrivateOutlet />}>
              <Route
                path="/users"
                element={
                  <HelmetWrapper title="Users" content="users">
                    <UsersPage />
                  </HelmetWrapper>
                }
              />
            </Route>
            <Route path="/user/profile" element={<PrivateOutlet />}>
              <Route
                path="/user/profile"
                element={
                  <HelmetWrapper title="User Profile" content="user">
                    <UserProfilePage />
                  </HelmetWrapper>
                }
              />
            </Route>
            <Route path="/market-prices/MappingSchema/edit" element={<Navigate to="/market-prices/MappingSchema" />} />
            <Route path="/market-prices/reports/excel" element={<Navigate to="/market-prices/reports" />} />
            <Route path="/gorp/gorpReports/excel" element={<Navigate to="/gorp/gorpReports" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </MsalNavigationWrapper>
      </Router>
    </HelmetProvider>
  )
}

export default App
